import { useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronRight } from '@fortawesome/free-solid-svg-icons';

import { TagAllowedEntry, TagAllowedNode } from '@/core/notifications/model';
import { entryLeafKeys, normalizeAllowedEntry } from '@/core/notifications/model/tag-schema';
import { CheckboxRow } from '@/shared/components/export';

// groups with many leaves start collapsed to keep the form scannable
export const COLLAPSE_THRESHOLD = 8;

export default function TagAllowedTree({
  entries,
  selected,
  onToggleLeaf,
  onToggleGroup,
  depth = 0,
  fieldIsLarge,
}: {
  entries: TagAllowedEntry[];
  selected: string[];
  onToggleLeaf: (leafKey: string) => void;
  onToggleGroup: (leafKeys: string[], checked: boolean) => void;
  depth?: number;
  fieldIsLarge?: boolean;
}) {
  const nodes = useMemo(() => entries.map(normalizeAllowedEntry), [entries]);

  // computed once, at the true root call — nested recursive calls always pass their
  // resolved value through explicitly so the decision reflects the whole field's size,
  // not just the leaf count of the subtree being rendered at this depth
  const leafCount = useMemo(() => entries.flatMap(entryLeafKeys).length, [entries]);
  const isLarge = fieldIsLarge ?? leafCount > COLLAPSE_THRESHOLD;

  return (
    <div className={depth > 0 ? 'ms-3' : undefined}>
      {nodes.map(node =>
        node.children ? (
          <GroupNode
            key={node.key}
            node={node}
            selected={selected}
            onToggleLeaf={onToggleLeaf}
            onToggleGroup={onToggleGroup}
            depth={depth}
            fieldIsLarge={isLarge}
          />
        ) : (
          <CheckboxRow
            key={node.key}
            checked={selected.includes(node.key)}
            onChange={() => onToggleLeaf(node.key)}
            label={node.name ?? node.key}
            title={node.key}
          />
        ),
      )}
    </div>
  );
}

function GroupNode({
  node,
  selected,
  onToggleLeaf,
  onToggleGroup,
  depth,
  fieldIsLarge,
}: {
  node: TagAllowedNode;
  selected: string[];
  onToggleLeaf: (leafKey: string) => void;
  onToggleGroup: (leafKeys: string[], checked: boolean) => void;
  depth: number;
  fieldIsLarge: boolean;
}) {
  const leafKeys = useMemo(() => entryLeafKeys(node), [node]);
  // a large field forces its top-level groups to start collapsed regardless of their
  // own size, so a taxonomy with many small groups (e.g. topicQcode) doesn't dump every
  // checkbox on screen at once; nested groups still expand on their own merits
  const [expanded, setExpanded] = useState(
    depth === 0 && fieldIsLarge ? false : leafKeys.length <= COLLAPSE_THRESHOLD,
  );

  const selectedCount = leafKeys.filter(key => selected.includes(key)).length;
  const checked = selectedCount > 0 && selectedCount === leafKeys.length;
  const indeterminate = selectedCount > 0 && selectedCount < leafKeys.length;

  return (
    <div>
      <div className="d-flex align-items-center gap-2">
        <CheckboxRow
          className="fw-semibold"
          checked={checked}
          indeterminate={indeterminate}
          onChange={next => onToggleGroup(leafKeys, next)}
          label={node.name ?? node.key}
          title={node.key}
        />
        <button
          type="button"
          className="btn btn-link btn-sm p-0 text-secondary"
          onClick={() => setExpanded(prev => !prev)}
          aria-label={expanded ? 'Collapse' : 'Expand'}
        >
          <FontAwesomeIcon icon={expanded ? faChevronDown : faChevronRight} size="xs" />
        </button>
        {!expanded && (
          <span className="small text-muted">
            {selectedCount} / {leafKeys.length} selected
          </span>
        )}
      </div>
      {expanded && (
        <TagAllowedTree
          entries={node.children ?? []}
          selected={selected}
          onToggleLeaf={onToggleLeaf}
          onToggleGroup={onToggleGroup}
          depth={depth + 1}
          fieldIsLarge={fieldIsLarge}
        />
      )}
    </div>
  );
}
